import { useState, useEffect } from 'react'
import { Customer, Organisation, Campaign } from '../../services/api'
import api from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { DomainVerificationBanner } from '../common'
import NewCampaignModal from '../NewCampaignModal'
import CampaignDetailsModal from '../CampaignDetailsModal'
import { createLogger } from '../../utils/createLogger'

const log = createLogger('CampaignsView')

// TODO: Implement proactive refresh mechanism
// Currently, campaigns created in the customer portal appear immediately here,
// but campaigns deleted from admin portal don't disappear until manual refresh.
// Additionally, campaign list shows stale data (original values) while the detail
// panel shows updated values after edits in admin portal - suggests caching issue
// in the list endpoint or component state not invalidating properly.
// Consider:
// - Polling for campaign list changes (every 30-60 seconds when page is active)
// - WebSocket/SignalR for real-time updates
// - Optimistic UI updates with server reconciliation
// - Event-based refresh system across browser tabs
// - Investigate why list and detail endpoints return different data after edits

interface CampaignsViewProps {
  organisation: Organisation
  customer: Customer
  isTrialMode: boolean
  onOrganisationUpdate?: () => void
}

const CampaignsView: React.FC<CampaignsViewProps> = ({
  organisation,
  customer,
  isTrialMode,
  onOrganisationUpdate
}) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [showNewCampaign, setShowNewCampaign] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null)

  useEffect(() => {
    loadCampaigns()
  }, [organisation.organisation_id, organisation.domain_verified])

  const loadCampaigns = async () => {
    try {
      setLoading(true)
      const data = await api.campaigns.list(organisation.organisation_id)
      setCampaigns(data)
    } catch (error) {
      log.error('Failed to load campaigns', error instanceof Error ? error : undefined, { organisationId: organisation.organisation_id })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Domain Verification Banner */}
      <DomainVerificationBanner 
        organisation={organisation} 
        onUpdate={loadCampaigns}
        onVerificationComplete={onOrganisationUpdate}
      />

      {/* Trial mode banner */}
      {isTrialMode && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1 text-gray-900 dark:text-gray-100">
              <span className="font-medium">Trial Mode:</span> Campaigns will only send emails to your admin account.
            </div>
            <button
              onClick={() => window.location.href = '/settings'}
              className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-sm rounded transition-colors"
            >
              Upgrade for £20/month
            </button>
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Campaigns</h2>
        <button
          onClick={() => setShowNewCampaign(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded transition-colors"
        >
          New Campaign
        </button>
      </div>

      {/* Campaign list */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        {campaigns.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No campaigns yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Create your first phishing simulation campaign</p>
            <button
              onClick={() => setShowNewCampaign(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded transition-colors"
            >
              Create Campaign
            </button>
          </div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Targets
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Click Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {campaigns.map(campaign => (
                <tr
                  key={campaign.campaign_id}
                  onClick={() => setSelectedCampaign(campaign.campaign_id)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {campaign.campaign_display_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full
                      ${campaign.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                      ${campaign.status === 'scheduled' ? 'bg-blue-100 text-blue-800' : ''}
                      ${campaign.status === 'complete' ? 'bg-gray-100 text-gray-800' : ''}
                      ${campaign.status === 'draft' ? 'bg-yellow-100 text-yellow-800' : ''}
                    `}>
                      {campaign.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="group relative">
                      <span
                        title={(campaign.target_selection_method || campaign.targets?.method) === 'manual' && campaign.campaign_targets && campaign.campaign_targets.length > 0
                          ? `Manual selection: ${campaign.campaign_targets.slice(0, 5).join(', ')}${campaign.campaign_targets.length > 5 ? ` and ${campaign.campaign_targets.length - 5} more` : ''}`
                          : undefined
                        }
                      >
                        {campaign.target_sample_size || campaign.target_count || campaign.campaign_targets?.length || campaign.targets?.emails?.length || 0}
                      </span>
                      
                      {/* Show tooltip for manual selection */}
                      {(campaign.target_selection_method || campaign.targets?.method) === 'manual' && 
                       campaign.campaign_targets && campaign.campaign_targets.length > 0 && (
                        <div className="absolute z-10 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity bg-gray-900 text-white text-xs rounded py-2 px-3 bottom-full left-0 mb-2 whitespace-nowrap max-w-sm">
                          <div className="font-medium mb-1">Selected targets:</div>
                          {campaign.campaign_targets.slice(0, 5).map((email, i) => (
                            <div key={i}>{email}</div>
                          ))}
                          {campaign.campaign_targets.length > 5 && (
                            <div className="mt-1 text-gray-300">...and {campaign.campaign_targets.length - 5} more</div>
                          )}
                          <div className="absolute bottom-0 left-4 transform translate-y-full">
                            <div className="border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {campaign.aggregated_results?.click_rate?.toFixed(1) || '0'}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(campaign.created_at || campaign.created_date || '').toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {showNewCampaign && (
        <NewCampaignModal
          organisation={organisation}
          isTrialMode={isTrialMode}
          onClose={() => setShowNewCampaign(false)}
          onSuccess={() => {
            setShowNewCampaign(false)
            loadCampaigns()
          }}
        />
      )}

      {selectedCampaign && (
        <CampaignDetailsModal
          campaignId={selectedCampaign}
          organisation={organisation}
          onClose={() => setSelectedCampaign(null)}
          onUpdate={() => {
            loadCampaigns()
            setSelectedCampaign(null)
          }}
        />
      )}
    </div>
  )
}

export default CampaignsView
